/**
 * Renders one printed box of a form.
 *
 * Bindings go through `Controller` rather than `register` because the shadcn
 * primitives are plain function components: on React 18 a `ref` arriving via
 * `{...register()}` is dropped with a warning, leaving the field untracked.
 * This follows the ICS-204 card, which hit exactly that.
 */

import type React from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { SparklesIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Field, FieldDescription, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import { ClickableFieldCitations } from 'components/DisasterGPT/fieldDraft/ClickableFieldCitations';
import { useAiFieldProvenance } from 'components/DisasterGPT/fieldDraft/useAiFieldProvenance';
import { ReportsGeneratingPlaceholder } from 'components/Reports/ReportsGeneratingPlaceholder';
import { useActualIncidentId } from '../hooks/commonHooks';

import { OperationalPeriodScheduleControl } from 'components/IAP/planningView/widgets/OperationalPeriodScheduleControl';
import type { FormSection } from './formSectionSchema';
import { sectionHeading } from './formSectionSchema';
import { useIcs202Draft } from './Ics202DraftContext';
import { Ics202DraftTarget } from './Ics202DraftFlow';
import {
  ICS_202_OMIT_SOURCES,
  ICS_202_SLOT_SOURCES,
} from './ics202DraftPrompt';
import { controlFor, textareaRows } from './sectionPresentation';
import { DescriptorSectionTable } from './DescriptorSectionTable';

/**
 * The printed label repeats the box title for most boxes, so it is only worth
 * showing when it carries the extra instruction the form prints beside it.
 */
const helperText = (section: FormSection): string | null => {
  const heading = sectionHeading(section);
  const printed = section.printedLabel.replace(/:$/, '');
  if (printed === heading || printed === section.label) return null;
  return printed.length > heading.length + 8 ? section.printedLabel : null;
};

export type DescriptorSectionFieldProps = {
  section: FormSection;
  formBaseId?: string;
  disabled?: boolean;
  /** Replaces the generated control, for boxes an existing component owns. */
  slot?: React.ReactNode;
};

const SectionAiChrome: React.FC<{
  section: FormSection;
  formBaseId?: string;
  heading: string;
}> = ({ section, formBaseId, heading }) => {
  const draft = useIcs202Draft();
  const canDraft =
    !!draft &&
    !draft.disabled &&
    formBaseId === 'ICS_202' &&
    !ICS_202_SLOT_SOURCES.has(section.source) &&
    !ICS_202_OMIT_SOURCES.has(section.source) &&
    section.kind !== 'auto';
  const generating = !!draft?.generatingSources.has(section.source);
  return (
    <div className="tw:flex tw:items-center tw:gap-1">
      {canDraft ? (
        <Button
          type="button"
          variant="ghost"
          size="icon-xs"
          aria-label={`Draft ${heading} with AI`}
          disabled={generating}
          onClick={() =>
            draft.openDraft([section.source], heading.replace(/:\s*$/, ''))
          }
        >
          <SparklesIcon
            className={generating ? 'tw:animate-spin' : undefined}
          />
        </Button>
      ) : null}
    </div>
  );
};

const InFieldCitations: React.FC<{
  source: string;
  formBaseId?: string;
}> = ({ source, formBaseId }) => {
  const incidentId = useActualIncidentId();
  const { data: provenance = [] } = useAiFieldProvenance(
    'incident_info',
    incidentId
  );
  if (formBaseId !== 'ICS_202') return null;
  const row = provenance.find((item) => item.fieldKey === source);
  if (!row) return null;
  return <ClickableFieldCitations citations={row.sources ?? []} />;
};

const useSectionDraftLock = (source: string, disabled: boolean) => {
  const draft = useIcs202Draft();
  const generating = !!draft?.generatingSources.has(source);
  return {
    generating,
    locked: disabled || generating,
  };
};

export const DescriptorSectionField: React.FC<DescriptorSectionFieldProps> = ({
  section,
  formBaseId,
  disabled = false,
  slot,
}) => {
  const { control } = useFormContext();
  const id = `${section.source}-field`;
  const heading = sectionHeading(section);
  const helper = helperText(section);
  const { generating, locked } = useSectionDraftLock(section.source, disabled);
  const draftChrome = generating ? <ReportsGeneratingPlaceholder /> : null;
  const outputFrame = (node: React.ReactElement): React.ReactElement =>
    formBaseId === 'ICS_202' ? (
      <Ics202DraftTarget source={section.source}>{node}</Ics202DraftTarget>
    ) : (
      node
    );
  const aiAttrs = {
    'data-section': section.source,
    'data-ai-target': 'section',
    'data-ai-form': formBaseId,
    'data-ai-section': section.source,
    'data-ai-label': heading,
  } as const;

  if (slot) {
    return outputFrame(
      <Field className="tw:gap-2" {...aiAttrs}>
        <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
          <FieldLabel htmlFor={id}>{heading}</FieldLabel>
          <SectionAiChrome
            section={section}
            formBaseId={formBaseId}
            heading={heading}
          />
        </div>
        {helper ? <FieldDescription>{helper}</FieldDescription> : null}
        {slot}
        <InFieldCitations source={section.source} formBaseId={formBaseId} />
        {draftChrome}
      </Field>
    );
  }

  if (section.kind === 'table') {
    return outputFrame(
      <DescriptorSectionTable
        section={section}
        disabled={disabled}
        heading={heading}
        helper={helper}
      />
    );
  }

  if (section.kind === 'boolean') {
    return outputFrame(
      <div className="tw:flex tw:flex-col tw:gap-2" {...aiAttrs}>
        <Field orientation="horizontal" className="tw:gap-3">
          <Controller
            control={control}
            name={section.source}
            render={({ field }) => (
              <Switch
                id={id}
                disabled={locked}
                checked={Boolean(field.value)}
                onCheckedChange={(checked) => field.onChange(checked)}
              />
            )}
          />
          <FieldLabel htmlFor={id} className="tw:font-normal">
            {heading}
          </FieldLabel>
          <SectionAiChrome
            section={section}
            formBaseId={formBaseId}
            heading={heading}
          />
        </Field>
        <InFieldCitations source={section.source} formBaseId={formBaseId} />
        {draftChrome}
      </div>
    );
  }

  const multiline = controlFor(section) === 'textarea';

  return outputFrame(
    <Field className="tw:gap-2" {...aiAttrs}>
      <div className="tw:flex tw:items-center tw:justify-between tw:gap-2">
        <FieldLabel htmlFor={id}>{heading}</FieldLabel>
        <SectionAiChrome
          section={section}
          formBaseId={formBaseId}
          heading={heading}
        />
      </div>
      {helper ? <FieldDescription>{helper}</FieldDescription> : null}
      <Controller
        control={control}
        name={section.source}
        render={({ field }) =>
          multiline ? (
            <Textarea
              id={id}
              rows={textareaRows(section)}
              disabled={locked}
              placeholder={`Enter ${section.label.toLowerCase()}...`}
              name={field.name}
              value={(field.value as string | undefined) ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          ) : (
            <Input
              id={id}
              disabled={locked}
              placeholder={`Enter ${section.label.toLowerCase()}...`}
              name={field.name}
              value={(field.value as string | undefined) ?? ''}
              onChange={field.onChange}
              onBlur={field.onBlur}
            />
          )
        }
      />
      <InFieldCitations source={section.source} formBaseId={formBaseId} />
      {draftChrome}
    </Field>
  );
};

/** A box printed from incident data rather than typed into the card. */
export const DescriptorAutoField: React.FC<{
  section: FormSection;
  value?: string | null;
}> = ({ section, value }) => {
  if (section.source === 'operationalPeriod') {
    return (
      <Field className="tw:gap-1" data-section={section.source}>
        <FieldLabel className="tw:text-muted-foreground">
          {sectionHeading(section)}
        </FieldLabel>
        <OperationalPeriodScheduleControl testId="fema-operational-period" />
      </Field>
    );
  }

  return (
    <Field className="tw:gap-1" data-section={section.source}>
      <FieldLabel className="tw:text-muted-foreground">
        {sectionHeading(section)}
      </FieldLabel>
      <span
        className={cn(
          'tw:block tw:min-w-0 tw:break-words',
          value ? 'tw:text-foreground' : 'tw:text-muted-foreground tw:italic'
        )}
      >
        {value?.trim() ? value : 'Filled on export'}
      </span>
    </Field>
  );
};
